/* eslint-disable no-unused-vars */
import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import { toast } from "react-toastify";
import "react-toastify/ReactToastify.css";

import {
  fetchEmployeeById,
  updateEmployeeById,
  createEmployee,
  clearEmployee,
  checkEmployeeCompleteness,
} from "../../../../store/slices/employeeDetailSlice";
import { fetchBranchesByCompanyId } from "../../../../store/slices/branchSlice";
import { fetchCompanyByType } from "../../../../store/slices/companySlice";
import { fetchDepartments } from "../../../../store/slices/departmentSlice";
import { fetchJobTitles } from "../../../../store/slices/jobtitleSlice";
import { fetchClients } from "../../../../store/slices/clientSlice";
import {
  uploadEmployeeImage,
  fetchEmployeeImageById,
  clearEmployeeImage,
} from "../../../../store/slices/employeeImageSlice";
import { Card, CardBody, CardTitle } from "react-bootstrap";
import LeaveTypeView from "../../../../common/components/LeaveManagement/LeaveTypeView";
import SubmitLeaveModal from "../../../../common/components/Test Component/testCreateLeaveRequest";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { leaveService } from "../../../../api/LeaveService/leaveService";
import { leaveApprovalService } from "../../../../api/LeaveService/leaveApproval"; 

const Section = ({ title, children, icon }) => (
  <div className="mb-4">
    <div
      className="d-flex align-items-center px-3 py-2 rounded-2 mb-3"
      style={{ backgroundColor: "#1e293b" }}
    >
      <i
        className={`bi ${icon} text-white me-2`}
        style={{ fontSize: "0.85rem" }}
      ></i>
      <h6
        className="m-0 text-white text-uppercase"
        style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
      >
        {title}
      </h6>
    </div>
    <div className="row g-3 px-2">{children}</div>
  </div>
);

// --- Sub-component refactored to use TanStack Query ---
const LeaveRow = ({ leave, handleApprove, handleReject, handleCancel }) => {
  // Extract the ID safely from the object structure
let userId = null;
  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      userId = typeof parsedUser.sub === 'function' ? parsedUser.sub() : parsedUser.sub;
    }
  } catch (e) {
    console.error("Failed to parse user from localStorage", e);
  }

  // 1. Log the incoming props to check if your IDs are correct
  console.log("LeaveRow Props -> currentUserId:", userId, "leaveId:", leave?.id);

  const { data: canManage = false, isLoading: loadingPermission, error } = useQuery({
    queryKey: ["checkApprover", userId, leave?.id],
    queryFn: async () => {
      const response = await leaveApprovalService.checkApprover({
        userId: userId,
        leaveRequestId: leave.id,
      });
      
      // 2. Log the raw response coming back from your API
      console.log("Raw API response for leave:", leave?.id, response);

      return typeof response === 'boolean' ? response : response?.canApprove ?? false;
    },
    // Ensure enabled checks the nested .sub property safely
    enabled: !!userId && !!leave?.id, 
    staleTime: 1000 * 60 * 5, 
  });

  // 3. Log what the final resolved boolean state is after parsing
  console.log("Can Manage result for leave:", leave?.id, "=>", canManage, "| Loading:", loadingPermission);

  if (error) {
    console.error("Error checking approver for leave:", leave?.id, error);
  }

  return (
    <tr>
      <td className="fw-bold">{leave.leaveType?.name || "N/A"}</td>
      <td>{leave.reason || "N/A"}</td>
      <td>{leave.startDate || "N/A"}</td>
      <td>{leave.endDate || "N/A"}</td>
      <td>{leave.totalDays || "0"}</td> 
      
      <td className="text-center">
        <span className={`badge text-uppercase ${leave.status === "approved" ? "bg-success" : "bg-warning"}`}>
          {leave.status || "Pending"}
        </span>
      </td>

      <td className="text-center">
        {loadingPermission ? (
          <span className="spinner-border spinner-border-sm text-secondary" role="status" />
        ) : (
          canManage && (
            <>
              {leave.status === "pending" && (
                <>
                  <button 
                    className="btn btn-sm btn-outline-success fw-bold m-1"
                    style={{ fontSize: "0.75rem", borderRadius: "6px" }}
                    onClick={() => handleApprove(leave.id)}
                    type="button"
                  >
                    Approve
                  </button>
                  <button 
                    className="btn btn-sm btn-outline-danger fw-bold m-1"
                    style={{ fontSize: "0.75rem", borderRadius: "6px" }}
                    onClick={() => handleReject(leave.id)}
                    type="button"
                  >
                    Reject
                  </button>
                </>
              )}

              {leave.status === "approved" && (
                <button 
                  className="btn btn-sm btn-outline-warning fw-bold m-1"
                  style={{ fontSize: "0.75rem", borderRadius: "6px" }}
                  onClick={() => handleCancel(leave.id)}
                  type="button"
                >
                  Cancel
                </button>
              )}
            </>
          )
        )}
      </td>
    </tr>
  );
};

const EmployeeLeaveListView = forwardRef((props, ref) => {
  const { id: propId, onRefresh } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routeParams = useParams();
  
  const id = propId || routeParams.id;

  const currentUserId = localStorage.getItem("user");

  const year = new Date().getFullYear();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState(null);
  const fileInputRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  const [leaveTypeFilter, setLeaveTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const handleOpen = (leave) => {
    setSelectedLeave(leave); 
    setShowModal(true);
  };

  const { employee } = useSelector((state) => state.employeeDetail);
  const { list: allOrgs = [] } = useSelector((state) => state.companyTypeList);
  const { list: allBranches = [] } = useSelector((state) => state.branch);
  const { list: departments = [] } = useSelector((state) => state.department);
  const { list: jobTitlesList = [] } = useSelector((state) => state.jobtitle);
  const { list: clientList = [] } = useSelector((state) => state.client);
  const image = useSelector((state) => state.employeeImage?.image);

  const { data: leaveList = [], isLoading } = useQuery({
    queryKey: ["leaveList", id],
    queryFn: async () => {
      const response = await leaveService.getEmployeeLeaveRequests(id);
      return Array.isArray(response) ? response : response.data || [];
    },
    enabled: !!id,
  });

  const uniqueLeaveTypes = useMemo(() => {
    const typesMap = new Map();
    leaveList.forEach((leave) => {
      if (leave.leaveType && leave.leaveType.id) {
        typesMap.set(leave.leaveType.id, leave.leaveType.name);
      }
    });
    return Array.from(typesMap, ([id, name]) => ({ id, name }));
  }, [leaveList]);

  const uniqueStatuses = useMemo(() => {
    const statusesSet = new Set();
    leaveList.forEach((leave) => {
      if (leave.status) {
        statusesSet.add(leave.status);
      }
    });
    return Array.from(statusesSet);
  }, [leaveList]);

  const filteredLeaveList = useMemo(() => {
    return leaveList.filter((leave) => {
      const matchesLeaveType = 
        leaveTypeFilter === "all" || leave.leaveType?.id === leaveTypeFilter;
      const matchesStatus = 
        statusFilter === "all" || leave.status === statusFilter;
      return matchesLeaveType && matchesStatus;
    });
  }, [leaveList, leaveTypeFilter, statusFilter]);

  const [isEditing, setIsEditing] = useState(!id);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [preview, setPreview] = useState(
    "https://placehold.co/200x200?text=Avatar",
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    extension: "",
    employeeId: "",
    employeeStatus: "Active",
    companyEmail: "",
    personalEmail: "",
    companyId: "",
    branchId: "",
    departmentId: "",
    jobTitle: "",
    clientCode: "",
    hiringDate: "",
    resignationDate: "",
    separationDate: "",
    terminationDate: "",
    probationaryDate: "",
    regularizationDate: "",
    remarks: "",
    assignmentType: "Internal",
    clientId: "",
    isSeasonal: false,
  });

  useEffect(() => {
    dispatch(fetchCompanyByType());
    dispatch(fetchBranchesByCompanyId());
    dispatch(fetchJobTitles());
    dispatch(fetchClients());
    if (id) {
      dispatch(fetchEmployeeById(id));
      dispatch(fetchEmployeeImageById(id));
    }
    return () => {
      dispatch(clearEmployee());
      dispatch(clearEmployeeImage());
      hasLoadedRef.current = false;
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (form.companyId) dispatch(fetchDepartments(form.companyId));
  }, [form.companyId, dispatch]);

  useEffect(() => {
    if (employee) {
      const matchedJobTitle = jobTitlesList.find(
        (jt) => jt.id === employee.jobTitleId,
      );

      setForm({
        ...employee,
        firstName: employee.firstName ?? "",
        middleName:
          employee.middleName === "string" || !employee.middleName
            ? ""
            : employee.middleName,
        lastName: employee.lastName ?? "",
        extension:
          employee.extension === "string" || !employee.extension
            ? ""
            : employee.extension,

        companyId: employee.companyId ?? "",
        branchId: employee.branchId ?? "",
        departmentId: employee.departmentId ?? "",
        clientId: employee.clientId ?? "",
        jobTitle: matchedJobTitle ? matchedJobTitle.title : "",
        clientCode: employee.client?.clientCode || "",
        assignmentType: employee.assignmentType ?? "Internal",
        employeeStatus: employee.employeeStatus ?? "Active",
        isSeasonal: employee.isSeasonal === true,
      });
      if (id && !hasLoadedRef.current) {
        hasLoadedRef.current = true;
        setIsEditing(false);
      }
    }
  }, [employee, id, jobTitlesList]);

  useEffect(() => {
    if (image?.imageUrl && !selectedFile) setPreview(image.imageUrl);
  }, [image, selectedFile]);

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  const validate = () => {
    let newErrors = {};
    if (!form.firstName) newErrors.firstName = true;
    if (!form.lastName) newErrors.lastName = true;
    if (!form.companyId) newErrors.companyId = true;
    if (!form.branchId) newErrors.branchId = true;
    if (!form.jobTitle) newErrors.jobTitle = true;

    if (form.assignmentType === "Internal") {
      if (!form.departmentId) newErrors.departmentId = true;
    } else {
      if (!form.clientId) newErrors.clientId = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) {
      setStatusMsg({ text: "Missing required fields.", type: "danger" });
      return;
    }

    setIsSaving(true);
    setStatusMsg({ text: "", type: "" });

    const payload = {
      firstName: form.firstName,
      middleName: form.middleName || "",
      lastName: form.lastName,
      extension: form.extension || "",
      companyId: form.companyId,
      branchId: form.branchId,
      assignmentType: form.assignmentType,
      departmentId:
        form.assignmentType === "Internal" ? form.departmentId : null,
      clientId: form.assignmentType === "Deployed" ? form.clientId : null,
      isSeasonal: form.assignmentType === "Deployed" ? form.isSeasonal : false,
      jobTitleId:
        jobTitlesList.find((jt) => jt.title === form.jobTitle)?.id || null,
      remarks: form.remarks || "",
      employeeStatus: form.employeeStatus,
    };

    try {
      let result;
      let targetId = id;

      if (id) {
        result = await dispatch(
          updateEmployeeById({ id, data: payload }),
        ).unwrap();
      } else {
        result = await dispatch(createEmployee(payload)).unwrap();
        targetId = result.id;
        hasLoadedRef.current = false;
      }

      if (selectedFile) {
        const imageData = new FormData();
        imageData.append("file", selectedFile);
        await dispatch(
          uploadEmployeeImage({ employeeId: targetId, file: imageData }),
        ).unwrap();
        setSelectedFile(null);
      }

      await dispatch(checkEmployeeCompleteness(targetId)).unwrap();

      setStatusMsg({
        text: "Saved and validated successfully!",
        type: "success",
      });
      setIsEditing(false);

      if (!id && targetId) {
        navigate(`/employee/${targetId}`, { replace: true });
      } else {
        dispatch(fetchEmployeeById(targetId));
      }

      setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
    } catch (err) {
      setStatusMsg({
        text: typeof err === "string" ? err : err?.message || "Error saving.",
        type: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const badgeStyle = (() => {
    const s = (form.employeeStatus || "").toLowerCase();
    if (s === "active" || s === "complete")
      return { bg: "#dcfce7", text: "#15803d", border: "#bbf7d0" };
    if (s === "incomplete" || s === "inactive")
      return { bg: "#fee2e2", text: "#b91c1c", border: "#fecaca" };
    return { bg: "#f1f5f9", text: "#475569", border: "#e2e8f0" };
  })();

  const handleApprove = async (requestId) => {
    try {
      await leaveService.approveLeave(requestId);
      await queryClient.invalidateQueries({ queryKey: ["leaveList", id] });
      await queryClient.invalidateQueries({ queryKey: ["employeeLeaveBalances", id] });
      if (onRefresh) onRefresh();
      toast.success("Leave approved successfully!", { containerId: "globalToast" });
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to approve leave", { containerId: "globalToast" });
    }
  };

  const handleCancel = async (requestId) => {
    try {
      await leaveService.cancelLeave(requestId);
      await queryClient.invalidateQueries({ queryKey: ["leaveList", id] });
      await queryClient.invalidateQueries({ queryKey: ["employeeLeaveBalances", id] });
      if (onRefresh) onRefresh();
      toast.success("Leave cancelled successfully!", { containerId: "globalToast" });
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to cancel leave", { containerId: "globalToast" });
    }
  };

  const handleReject = async (requestId) => {
    try {
      await leaveService.rejectLeave(requestId);
      await queryClient.invalidateQueries({ queryKey: ["leaveList", id] });
      await queryClient.invalidateQueries({ queryKey: ["employeeLeaveBalances", id] });
      if (onRefresh) onRefresh();
      toast.success("Leave rejected successfully!", { containerId: "globalToast" });
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to reject leave", { containerId: "globalToast" });
    }
  };

  return (
    <div className="container-fluid px-0">

      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        <div
          className="d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mb-4"
          style={{ backgroundColor: "#1e293b", minHeight: "44px" }}
        >
          <div className="d-flex align-items-center">
            <i
              className="bi bi-person-vcard-fill text-white me-2"
              style={{ fontSize: "0.9rem" }}
            ></i>
            <h6
              className="m-0 text-white text-uppercase"
              style={{ fontSize: "0.75rem" }}
            >
              Applied Leave
            </h6>
          </div>
          
          <button
            type="button"
            className="d-flex align-items-center bg-transparent border-0 p-0 text-start"
            style={{ cursor: "pointer", transition: "opacity 0.2s" }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            onClick={() => setIsSubmitModalOpen(true)}
          >
            <div className="d-flex align-items-center">
              <i
                className="bi bi-plus text-white me-2"
                style={{ fontSize: "0.9rem" }}
              ></i>
              <h6
                className="m-0 text-white text-uppercase"
                style={{ fontSize: "0.75rem" }}
              >
                Create Leave Request
              </h6>
            </div>
          </button>
        </div>

        {/* Filter Controls */}
        <div className="row g-3 mb-3 px-2">
          <div className="col-md-4">
            <label className="form-label small fw-bold">Filter by Leave Type</label>
            <select
              className="form-select form-select-sm"
              value={leaveTypeFilter}
              onChange={(e) => setLeaveTypeFilter(e.target.value)}
            >
              <option value="all">All Leave Types</option>
              {uniqueLeaveTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-bold">Filter by Status</label>
            <select
              className="form-select form-select-sm text-uppercase"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row g-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm bg-white">
                <thead className="table-light">
                  <tr>
                    <th>Leave Type</th>
                    <th>Reason</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Total Days</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeaveList && filteredLeaveList.length > 0 ? (
                    filteredLeaveList.map((leave) => (
                      <LeaveRow 
                        key={leave.id}
                        leave={leave}
                        currentUserId={currentUserId}
                        handleApprove={handleApprove}
                        handleReject={handleReject}
                        handleCancel={handleCancel}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        No leave requests found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </form>
      </div>

      {isSubmitModalOpen && (
        <SubmitLeaveModal 
          employeeId={id} 
          onClose={() => {
            setIsSubmitModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ["leaveList", id] });
            queryClient.invalidateQueries({ queryKey: ["employeeLeaveBalances", id] });
            if (onRefresh) onRefresh();
          }} 
        />
      )}

      {isModalOpen && (
        <LeaveTypeView
          employeeId={id} 
          leaveTypeId={selectedLeaveTypeId} 
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
});

export default EmployeeLeaveListView;